import { msg } from '@lingui/core/macro';
import type { Answer } from '@routes/sidebar/admin-center/creator/___pages___/elements/api/creatorElements.api.ts';
import { validateAnswers } from '@routes/sidebar/admin-center/creator/___pages___/elements/ui/questions/ui/creator/answers/utils.ts';
import { REQUIRED_ANSWER_TYPES } from '@routes/sidebar/admin-center/creator/___pages___/elements/ui/questions/ui/creator/constants.ts';
import { createCsvFieldHelpers } from '@shared/csv/use-caret-utils';
import { translateItem } from '@utils/translations.ts';

import {
    ACCENT_INSENSITIVE_COMPARE,
    ACCEPTED_QUIZ_TYPES,
    ACCEPTED_REQUIRED_ANSWER_TYPES,
    ANSWER_SLOTS,
    DECIMAL_RADIX,
    EXPECTED_COLUMN_COUNT,
    FINAL_QUIZ_TYPE,
    INCORRECT,
    MAX_ANSWER_OPTIONS,
    MAX_TOTAL_ANSWERS_TO_DISPLAY,
    MIN_TOTAL_ANSWERS_TO_DISPLAY,
    PRACTICE_QUIZ_TYPE,
    QUESTIONS_CSV_FIELD,
    QUESTIONS_CSV_IMPORT_STATUS_PARTIAL,
    QUESTIONS_CSV_IMPORT_STATUS_SUCCESS,
} from './constants';
import type { QuestionsCsvImportStatus } from './types';

export const SHARED_QUESTIONS_CSV_VALIDATION_VARIATION_REQUIRED = msg`QuestionVariation1 is required.`;

const { isEmpty: isCsvEmpty } = createCsvFieldHelpers();

const equalsInsensitive = (first: string, second: string): boolean =>
    first.trim().localeCompare(second.trim(), undefined, ACCENT_INSENSITIVE_COMPARE) === 0;

const isAcceptedQuizType = (value: string) => ACCEPTED_QUIZ_TYPES.some((x) => equalsInsensitive(x, value));

const isAcceptedRequiredAnswerType = (value: string) =>
    ACCEPTED_REQUIRED_ANSWER_TYPES.some((x) => equalsInsensitive(x, value));

export const isQuestionsCsvImportSuccessful = (status: QuestionsCsvImportStatus) =>
    status === QUESTIONS_CSV_IMPORT_STATUS_SUCCESS || status === QUESTIONS_CSV_IMPORT_STATUS_PARTIAL;

export const getCsvToRequiredAnswerType = (value: string): string => {
    if (equalsInsensitive(value, REQUIRED_ANSWER_TYPES.RANDOM)) {
        return REQUIRED_ANSWER_TYPES.RANDOM;
    }

    if (equalsInsensitive(value, REQUIRED_ANSWER_TYPES.WRONG)) {
        return REQUIRED_ANSWER_TYPES.WRONG;
    }

    return REQUIRED_ANSWER_TYPES.CORRECT;
};

export const parseAnswersFromCsvFields = (fields: string[]): Answer[] => {
    const answers: Answer[] = [];

    for (let i = 0; i < ANSWER_SLOTS; i++) {
        const text = fields[QUESTIONS_CSV_FIELD.correctAnswersStart + i];
        if (text && !isCsvEmpty(text)) {
            answers.push({ text, isCorrect: true, adminAnswerId: null });
        }
    }

    for (let i = 0; i < ANSWER_SLOTS; i++) {
        const text = fields[QUESTIONS_CSV_FIELD.wrongAnswersStart + i];
        if (text && !isCsvEmpty(text)) {
            answers.push({ text, isCorrect: false, adminAnswerId: null });
        }
    }

    return answers;
};

export const countFilledAnswersFromCsvFields = (fields: string[]): number => {
    let count = 0;

    for (let i = 0; i < ANSWER_SLOTS; i++) {
        const correct = fields[QUESTIONS_CSV_FIELD.correctAnswersStart + i];
        const wrong = fields[QUESTIONS_CSV_FIELD.wrongAnswersStart + i];

        if (correct && !isCsvEmpty(correct)) {
            count++;
        }

        if (wrong && !isCsvEmpty(wrong)) {
            count++;
        }
    }

    return count;
};

export const validateQuestionCsvRow = (fields: string[]): string | null => {
    const validationErrors: string[] = [];

    if (fields.length < EXPECTED_COLUMN_COUNT) {
        return translateItem(msg`Expected ${EXPECTED_COLUMN_COUNT} columns, found ${fields.length}.`);
    }

    const quizType = fields[QUESTIONS_CSV_FIELD.quizType]?.trim() ?? '';
    if (!quizType) {
        validationErrors.push(
            translateItem(msg`QuizType is required. Accepted values: '${FINAL_QUIZ_TYPE}' or '${PRACTICE_QUIZ_TYPE}'.`),
        );
    } else if (!isAcceptedQuizType(quizType)) {
        validationErrors.push(
            translateItem(msg`QuizType must be '${FINAL_QUIZ_TYPE}' or '${PRACTICE_QUIZ_TYPE}'. Got: '${quizType}'.`),
        );
    }

    const variation1 = fields[QUESTIONS_CSV_FIELD.variation1]?.trim() ?? '';
    if (!variation1 || isCsvEmpty(variation1)) {
        validationErrors.push(translateItem(SHARED_QUESTIONS_CSV_VALIDATION_VARIATION_REQUIRED));
    }

    const typeOfRequiredAnswerRaw = fields[QUESTIONS_CSV_FIELD.typeOfRequiredAnswer]?.trim() ?? '';
    if (!typeOfRequiredAnswerRaw) {
        validationErrors.push(
            translateItem(
                msg`TypeOfRequiredAnswer is required. Accepted values: '${REQUIRED_ANSWER_TYPES.CORRECT}' or '${REQUIRED_ANSWER_TYPES.WRONG}'.`,
            ),
        );
    } else if (!isAcceptedRequiredAnswerType(typeOfRequiredAnswerRaw)) {
        validationErrors.push(
            translateItem(
                msg`TypeOfRequiredAnswer must be '${REQUIRED_ANSWER_TYPES.CORRECT}' or '${REQUIRED_ANSWER_TYPES.WRONG}'. Got: '${typeOfRequiredAnswerRaw}'.`,
            ),
        );
    }

    const totalAnswersRaw = fields[QUESTIONS_CSV_FIELD.totalAnswersToSelect]?.trim() ?? '';
    const totalAnswers = Number.parseInt(totalAnswersRaw, DECIMAL_RADIX);
    if (!totalAnswersRaw || isCsvEmpty(totalAnswersRaw)) {
        validationErrors.push(translateItem(msg`TotalAnswersToSelect is required and must be a number.`));
    } else if (
        Number.isNaN(totalAnswers) ||
        totalAnswers < MIN_TOTAL_ANSWERS_TO_DISPLAY ||
        totalAnswers > MAX_TOTAL_ANSWERS_TO_DISPLAY
    ) {
        validationErrors.push(
            translateItem(
                msg`TotalAnswersToSelect must be a number between ${MIN_TOTAL_ANSWERS_TO_DISPLAY} and ${MAX_TOTAL_ANSWERS_TO_DISPLAY}. Got: '${totalAnswersRaw}'.`,
            ),
        );
    }

    const minCorrectRaw = fields[QUESTIONS_CSV_FIELD.minimumCorrectAnswersToSelect]?.trim() ?? '';
    const minCorrectAnswers = Number.parseInt(minCorrectRaw, DECIMAL_RADIX);
    if (!minCorrectRaw || isCsvEmpty(minCorrectRaw)) {
        validationErrors.push(translateItem(msg`MinimumCorrectAnswersToSelect is required and must be a number.`));
    } else if (Number.isNaN(minCorrectAnswers) || minCorrectAnswers < 1) {
        validationErrors.push(
            translateItem(msg`MinimumCorrectAnswersToSelect must be at least 1. Got: '${minCorrectRaw}'.`),
        );
    } else if (!Number.isNaN(totalAnswers) && minCorrectAnswers >= totalAnswers) {
        validationErrors.push(
            translateItem(
                msg`MinimumCorrectAnswersToSelect must be less than TotalAnswersToSelect (${totalAnswers}). Got: '${minCorrectRaw}'.`,
            ),
        );
    }

    const timeSecondsRaw = fields[QUESTIONS_CSV_FIELD.timeSeconds]?.trim() ?? '';
    if (timeSecondsRaw && !isCsvEmpty(timeSecondsRaw)) {
        const parsedTime = Number.parseInt(timeSecondsRaw, DECIMAL_RADIX);
        if (Number.isNaN(parsedTime) || parsedTime < 0) {
            validationErrors.push(translateItem(msg`TimeSeconds must be a non-negative number.`));
        }
    }

    if (validationErrors.length > 0) {
        return validationErrors.join(' ');
    }

    const filledAnswerCount = countFilledAnswersFromCsvFields(fields);

    if (filledAnswerCount > MAX_ANSWER_OPTIONS) {
        return translateItem(
            msg`A maximum of ${MAX_ANSWER_OPTIONS} answer options can be provided across CorrectAnswer and WrongAnswer columns. Found ${filledAnswerCount}.`,
        );
    }

    if (filledAnswerCount < MIN_TOTAL_ANSWERS_TO_DISPLAY) {
        return translateItem(
            msg`At least ${MIN_TOTAL_ANSWERS_TO_DISPLAY} answer options are required across CorrectAnswer and WrongAnswer columns. Found ${filledAnswerCount}.`,
        );
    }

    const requiredAnswerType = getCsvToRequiredAnswerType(typeOfRequiredAnswerRaw);
    const answers = parseAnswersFromCsvFields(fields);

    const answerValidation = validateAnswers({
        answers,
        minCorrectAnswers,
        requiredAnswerType,
        totalAnswersToDisplay: totalAnswers,
    });

    if (!answerValidation.hasEnoughAnswers) {
        return translateItem(
            msg`Provide at least ${totalAnswers} answer options to match TotalAnswersToSelect. Found ${filledAnswerCount}.`,
        );
    }

    if (!answerValidation.hasDistribution) {
        const isCorrectMode =
            requiredAnswerType === REQUIRED_ANSWER_TYPES.CORRECT || requiredAnswerType === REQUIRED_ANSWER_TYPES.RANDOM;
        const requiredLabel = isCorrectMode ? `'${REQUIRED_ANSWER_TYPES.CORRECT}'` : `'${INCORRECT}'`;
        const oppositeLabel = isCorrectMode ? `'${INCORRECT}'` : `'${REQUIRED_ANSWER_TYPES.CORRECT}'`;

        return translateItem(
            msg`Need at least ${minCorrectAnswers} ${requiredLabel} answer(s) and at least 1 ${oppositeLabel} answer. Found ${answerValidation.correctCount} ${requiredLabel} and ${answerValidation.incorrectCount} ${oppositeLabel}.`,
        );
    }

    if (!answerValidation.hasUniqueAnswerTexts) {
        return translateItem(msg`Answer texts must be unique.`);
    }

    return null;
};
